import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
  Filter,
  Mail,
  MoreHorizontal,
  Lock,
  Pencil,
  RefreshCw,
  Search,
  User,
  UserPlus,
  UserX,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRegistrations } from "@/hooks/admin/useRegistrations";
import {
  useApproveRegistration,
  useEditRegistration,
  useInactivateRegistration,
  useRejectRegistration,
} from "@/hooks/admin/useRegistrationMutations";

const PAGE_SIZE = 20;
const EMPTY_REGISTRATIONS = [];
const DEFAULT_PAGINATION = { page: 1, totalPages: 1, total: 0 };
const STATUS_FILTER_OPTIONS = [
  { value: "pending_verification", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All statuses" },
];

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (normalized === "inactive") return "Inactive";
  if (normalized === "pending_verification") return "Pending";
  if (normalized === "active") return "Active";
  if (normalized === "rejected") return "Rejected";
  return "Unknown";
};

const isInactiveAccount = (status, accountState) => {
  const normalizedState = String(accountState || "")
    .trim()
    .toLowerCase();

  if (normalizedState) {
    return normalizedState === "inactive";
  }

  const normalizedStatus = String(status || "")
    .trim()
    .toLowerCase();

  return normalizedStatus === "inactive";
};

const getStatusClasses = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (normalized === "inactive") {
    return "bg-slate-100 text-slate-700";
  }

  if (normalized === "pending_verification") {
    return "bg-amber-100 text-amber-700";
  }

  if (normalized === "active") {
    return "bg-emerald-100 text-emerald-700";
  }

  if (normalized === "rejected") {
    return "bg-red-100 text-red-700";
  }

  return "bg-gray-100 text-gray-700";
};

export default function AccountRegistrations() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("pending_verification");
  const [page, setPage] = useState(1);

  const [approveTarget, setApproveTarget] = useState(null);
  const [inactivateTarget, setInactivateTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [editTarget, setEditTarget] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    clinicName: "",
    password: "",
    confirmPassword: "",
  });

  const { mutate: approveRegistration, isPending: isApproving } = useApproveRegistration();
  const { mutate: inactivateRegistration, isPending: isInactivating } = useInactivateRegistration();
  const { mutate: rejectRegistration, isPending: isRejecting } = useRejectRegistration();
  const { mutate: editRegistration, isPending: isEditing } = useEditRegistration();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search || undefined,
      status,
    }),
    [page, search, status]
  );

  const { data, isLoading, isFetching, refetch } = useRegistrations(queryParams);

  const registrations = data?.data ?? EMPTY_REGISTRATIONS;
  const pagination = data?.pagination ?? DEFAULT_PAGINATION;

  const statusStats = useMemo(() => {
    const pending = registrations.filter((row) => row.status === "pending_verification").length;
    const active = registrations.filter((row) => row.status === "active").length;
    const rejected = registrations.filter((row) => row.status === "rejected").length;
    const inactive = registrations.filter((row) =>
      isInactiveAccount(row.status, row.accountState)
    ).length;

    return {
      pending,
      active,
      rejected,
      inactive,
    };
  }, [registrations]);

  const selectedStatusLabel =
    STATUS_FILTER_OPTIONS.find((option) => option.value === status)?.label || "All statuses";

  const closeRejectDialog = () => {
    setRejectTarget(null);
    setRejectReason("");
  };

  const openEditDialog = (account) => {
    setEditTarget(account);
    setEditForm({
      name: account?.name || "",
      email: account?.email || "",
      clinicName: account?.clinicName || "",
      password: "",
      confirmPassword: "",
    });
  };

  const closeEditDialog = () => {
    setEditTarget(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setEditForm({
      name: "",
      email: "",
      clinicName: "",
      password: "",
      confirmPassword: "",
    });
  };

  const handleSubmitEdit = (e) => {
    e.preventDefault();

    const payload = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      clinicName: editForm.clinicName.trim(),
    };

    if (!payload.name || !payload.email || !payload.clinicName) {
      toast.error("Missing fields", {
        description: "Name, email, and clinic name are required.",
      });
      return;
    }

    const passwordValue = String(editForm.password || "");
    const confirmPasswordValue = String(editForm.confirmPassword || "");
    const hasPasswordInput = Boolean(passwordValue || confirmPasswordValue);

    if (hasPasswordInput) {
      if (passwordValue.length < 8) {
        toast.error("Invalid password", {
          description: "Password must be at least 8 characters.",
        });
        return;
      }

      if (passwordValue !== confirmPasswordValue) {
        toast.error("Password mismatch", {
          description: "Password and confirm password must match.",
        });
        return;
      }

      payload.password = passwordValue;
    }

    editRegistration(
      {
        id: editTarget.id,
        payload,
      },
      {
        onSuccess: () => {
          closeEditDialog();
        },
      }
    );
  };

  return (
    <div className="mx-auto w-full max-w-400 space-y-6 p-4 sm:space-y-8 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl md:text-4xl">
            Account Registrations
          </h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base md:text-lg">
            Monitor newly created Vet Assist accounts and verify them from one place.
          </p>
        </div>

        <Button
          variant="outline"
          size="lg"
          onClick={() => refetch()}
          disabled={isFetching}
          className="w-full sm:w-auto cursor-pointer">
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Matched Accounts
          </p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{pagination.total}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Pending In View
          </p>
          <p className="mt-2 text-3xl font-bold text-amber-600">{statusStats.pending}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Active In View
          </p>
          <p className="mt-2 text-3xl font-bold text-emerald-600">{statusStats.active}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Rejected In View
          </p>
          <p className="mt-2 text-3xl font-bold text-red-600">{statusStats.rejected}</p>
        </div>
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            Inactive In View
          </p>
          <p className="mt-2 text-3xl font-bold text-slate-700">{statusStats.inactive}</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border bg-white p-4 shadow-sm lg:flex-row lg:items-center">
        <div className="relative w-full lg:flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, or clinic..."
            className="h-11 w-full rounded-md border bg-white pr-4 pl-9 text-sm transition-colors outline-none focus:border-blue-500"
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="lg"
              className="h-11 w-full justify-between border-dashed sm:w-auto sm:min-w-40 cursor-pointer">
              <div className="flex items-center">
                <Filter className="text-muted-foreground mr-2 h-4 w-4" />
                <span className="truncate">{selectedStatusLabel}</span>
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {STATUS_FILTER_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => {
                  setStatus(option.value);
                  setPage(1);
                }}
                className={status === option.value ? "bg-accent font-medium" : ""}>
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-xl border bg-white shadow-sm">
        <div className="hidden border-b px-6 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase md:grid md:grid-cols-[1.6fr_1.6fr_1.5fr_1fr_1.3fr_1.3fr_auto] md:gap-4">
          <span>Name</span>
          <span>Email</span>
          <span>Clinic</span>
          <span>Status</span>
          <span>Created At</span>
          <span>Reviewed At</span>
          <span></span>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-gray-500">Loading registrations...</div>
        ) : registrations.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <UserPlus className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-700">No accounts found.</p>
            <p className="mt-1 text-sm text-gray-500">
              Try changing your filters or wait for new registrations.
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {registrations.map((account) => {
              const normalizedStatus = String(account.status || "")
                .trim()
                .toLowerCase();
              const canApprove =
                normalizedStatus === "pending_verification" ||
                normalizedStatus === "inactive" ||
                normalizedStatus === "rejected";
              const canInactivate = normalizedStatus === "active";
              const canReject = normalizedStatus === "inactive";

              return (
                <div
                  key={account.id}
                  className="grid grid-cols-1 gap-3 px-6 py-4 md:grid-cols-[1.6fr_1.6fr_1.5fr_1fr_1.3fr_1.3fr_auto] md:items-center md:gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{account.name || "-"}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{account.email || "-"}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{account.clinicName || "-"}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className={getStatusClasses(account.status)}>
                      {formatStatus(account.status)}
                    </Badge>
                  </div>

                  <div className="text-sm text-gray-600">{formatDateTime(account.createdAt)}</div>

                  <div className="text-sm text-gray-600">{formatDateTime(account.reviewedAt)}</div>

                  <div>
                    <Tooltip>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                        {canApprove && (
                          <DropdownMenuItem
                            disabled={isApproving || isInactivating || isRejecting}
                            onClick={() => setApproveTarget(account)}>
                            <CheckCircle2 className="mr-2 h-4 w-4" />
                            {normalizedStatus === "inactive" || normalizedStatus === "rejected"
                              ? "Reactivate"
                              : "Approve"}
                          </DropdownMenuItem>
                        )}

                        {canInactivate && (
                          <DropdownMenuItem
                            disabled={isApproving || isInactivating || isRejecting}
                            onClick={() => setInactivateTarget(account)}>
                            <UserX className="mr-2 h-4 w-4" />
                            Set Inactive
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuItem
                          disabled={isEditing}
                          onClick={() => openEditDialog(account)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>

                        {canReject && (
                          <DropdownMenuItem
                            disabled={isApproving || isInactivating || isRejecting}
                            className="text-red-600 focus:text-red-600"
                            onClick={() => {
                              setRejectTarget(account);
                              setRejectReason(account?.rejectionReason || "");
                            }}>
                            <XCircle className="mr-2 h-4 w-4" />
                            Reject
                          </DropdownMenuItem>
                        )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <TooltipContent side="top">Account actions</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          Showing {registrations.length} of {pagination.total} accounts
        </p>

        <div className="flex items-center gap-2">
          <Button className="cursor-pointer"
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={pagination.page <= 1 || isFetching}>
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <span className="text-sm text-gray-600">
            Page {pagination.page} of {Math.max(1, pagination.totalPages)}
          </span>

          <Button className="cursor-pointer"
            variant="outline"
            size="sm"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={pagination.page >= Math.max(1, pagination.totalPages) || isFetching}>
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AlertDialog
        open={!!approveTarget}
        onOpenChange={(open) => {
          if (!open) setApproveTarget(null);
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve this account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will activate the account for
              <span className="font-medium text-gray-900"> {approveTarget?.email}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isApproving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isApproving}
              onClick={() => {
                if (!approveTarget) return;

                approveRegistration(approveTarget.id, {
                  onSettled: () => setApproveTarget(null),
                });
              }}>
              {isApproving ? "Approving..." : "Approve"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!inactivateTarget}
        onOpenChange={(open) => {
          if (!open) setInactivateTarget(null);
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set this account to inactive?</AlertDialogTitle>
            <AlertDialogDescription>
              This will block sign-in access for
              <span className="font-medium text-gray-900"> {inactivateTarget?.email}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isInactivating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isInactivating}
              onClick={() => {
                if (!inactivateTarget) return;

                inactivateRegistration(inactivateTarget.id, {
                  onSettled: () => setInactivateTarget(null),
                });
              }}>
              {isInactivating ? "Updating..." : "Set Inactive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) closeRejectDialog();
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this account?</AlertDialogTitle>
            <AlertDialogDescription>
              Provide a reason for rejecting
              <span className="font-medium text-gray-900"> {rejectTarget?.email}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason for rejection"
            className="min-h-24 w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-blue-500"
          />

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRejecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isRejecting || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                if (!rejectTarget) return;

                rejectRegistration(
                  {
                    id: rejectTarget.id,
                    reason: rejectReason.trim(),
                  },
                  {
                    onSettled: () => closeRejectDialog(),
                  }
                );
              }}>
              {isRejecting ? "Rejecting..." : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && closeEditDialog()}>
        {/* Added overflow-hidden and p-0 to take full control of the modal's internal layout */}
        <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle className="text-xl font-semibold">Edit account details</DialogTitle>
            <DialogDescription className="mt-1.5 text-sm">
              Update account information before approval.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitEdit} className="flex flex-col">
            {/* Added proper padding and vertical spacing for the inputs */}
            <div className="space-y-5 px-6 py-6">
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">Full Name</label>
                <div className="relative">
                  <User className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pr-3 pl-10 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
                    placeholder="e.g. John Doe"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">Email Address</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pr-3 pl-10 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
                    placeholder="e.g. john@clinic.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">Clinic Name</label>
                <div className="relative">
                  <Building2 className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    value={editForm.clinicName}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, clinicName: e.target.value }))
                    }
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pr-3 pl-10 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
                    placeholder="e.g. Happy Paws Clinic"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">New Password</label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={editForm.password}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, password: e.target.value }))}
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pr-10 pl-10 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
                    placeholder="Leave blank to keep current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Minimum 8 characters.</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-700">Confirm New Password</label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={editForm.confirmPassword}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                    }
                    className="h-10 w-full rounded-md border border-gray-300 bg-white pr-10 pl-10 text-sm text-gray-900 transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none"
                    placeholder="Repeat new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Added a subtle gray background, top border, and padding to anchor the buttons properly */}
            <DialogFooter className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditDialog}
                disabled={isEditing}
                className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isEditing}
                className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto">
                {isEditing ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
